import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RotateCcw, Trophy, Bot as BotIcon } from 'lucide-react';

const LINES = [[0,1,2],[3,4,5],[6,7,8],[0,3,6],[1,4,7],[2,5,8],[0,4,8],[2,4,6]];
function winnerOf(b) {
  for (const [a,c,d] of LINES) if (b[a] && b[a] === b[c] && b[a] === b[d]) return { winner: b[a], line: [a,c,d] };
  if (b.every(x => x)) return { winner: 'draw', line: null };
  return null;
}
function empties(b) { return b.map((v,i)=>v?null:i).filter(x=>x!==null); }

// Minimax for perfect play
function minimax(b, player, bot, human) {
  const w = winnerOf(b);
  if (w) {
    if (w.winner === bot) return { score: 10 };
    if (w.winner === human) return { score: -10 };
    return { score: 0 };
  }
  const moves = empties(b).map(i => {
    const nb = [...b]; nb[i] = player;
    const { score } = minimax(nb, player === bot ? human : bot, bot, human);
    return { i, score };
  });
  return moves.reduce((best, m) => {
    if (player === bot) return !best || m.score > best.score ? m : best;
    return !best || m.score < best.score ? m : best;
  }, null);
}

function pickBotMove(board, difficulty, bot, human) {
  const opts = empties(board);
  if (!opts.length) return -1;
  // Easy: 70% random, 30% smart
  if (difficulty === 'easy' && Math.random() < 0.7) return opts[Math.floor(Math.random() * opts.length)];
  // Medium: try to win / block, else random
  if (difficulty !== 'hard') {
    for (const sym of [bot, human]) {
      for (const [a,c,d] of LINES) {
        const line = [board[a], board[c], board[d]];
        if (line.filter(x => x === sym).length === 2 && line.includes('')) {
          const idx = [a,c,d][line.indexOf('')];
          if (opts.includes(idx)) return idx;
        }
      }
    }
    return opts[Math.floor(Math.random() * opts.length)];
  }
  // Hard: minimax
  const best = minimax(board, bot, bot, human);
  return best ? best.i : opts[0];
}

const EMPTY_BOARD = () => ['','','','','','','','',''];

export default function TicTacToeBot() {
  const [difficulty, setDifficulty] = useState('medium');
  const [playerSymbol, setPlayerSymbol] = useState('X');
  const [board, setBoard] = useState(EMPTY_BOARD);
  const [turn, setTurn] = useState('X'); // X always starts
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(() => {
    try { return JSON.parse(localStorage.getItem('spc_oxo_bot_score')) || { wins: 0, losses: 0, draws: 0 }; }
    catch { return { wins: 0, losses: 0, draws: 0 }; }
  });

  const bot = playerSymbol === 'X' ? 'O' : 'X';
  const myTurn = turn === playerSymbol && !result;

  const reset = useCallback((symbol) => {
    const s = symbol || playerSymbol;
    setBoard(EMPTY_BOARD());
    setTurn('X');
    setResult(null);
    setPlayerSymbol(s);
  }, [playerSymbol]);

  // Check for winner after each move
  useEffect(() => {
    const w = winnerOf(board);
    if (w && !result) {
      setResult(w);
      setScore(prev => {
        const next = { ...prev };
        if (w.winner === 'draw') next.draws += 1;
        else if (w.winner === playerSymbol) next.wins += 1;
        else next.losses += 1;
        try { localStorage.setItem('spc_oxo_bot_score', JSON.stringify(next)); } catch {}
        return next;
      });
    }
  }, [board, result, playerSymbol]);

  // Bot moves
  useEffect(() => {
    if (result) return;
    if (turn !== bot) return;
    const t = setTimeout(() => {
      const move = pickBotMove(board, difficulty, bot, playerSymbol);
      if (move < 0) return;
      setBoard(b => { const nb = [...b]; if (!nb[move]) nb[move] = bot; return nb; });
      setTurn(playerSymbol);
    }, 420);
    return () => clearTimeout(t);
  }, [turn, bot, board, difficulty, playerSymbol, result]);

  const play = (i) => {
    if (!myTurn || board[i] || result) return;
    setBoard(b => { const nb = [...b]; nb[i] = playerSymbol; return nb; });
    setTurn(bot);
  };

  const statusLine = useMemo(() => {
    if (!result) return myTurn ? 'Your move' : 'Coach Bot is thinking…';
    if (result.winner === 'draw') return "It's a draw";
    if (result.winner === playerSymbol) return 'You won! 🏆';
    return 'Coach Bot wins — rematch?';
  }, [result, myTurn, playerSymbol]);

  const resetScore = () => {
    const clean = { wins: 0, losses: 0, draws: 0 };
    setScore(clean);
    try { localStorage.setItem('spc_oxo_bot_score', JSON.stringify(clean)); } catch {}
  };

  return (
    <div className="solo-game" data-testid="solo-oxo">
      <div className="solo-head">
        <div className="solo-title"><BotIcon size={18} /><span>Noughts &amp; Crosses vs Coach Bot</span></div>
        <div className="solo-score" data-testid="solo-oxo-score">
          <span className="pill green">W {score.wins}</span>
          <span className="pill">D {score.draws}</span>
          <span className="pill red">L {score.losses}</span>
        </div>
      </div>

      <div className="solo-controls">
        <label>Difficulty
          <select value={difficulty} onChange={(e) => { setDifficulty(e.target.value); reset(); }} data-testid="solo-oxo-difficulty">
            <option value="easy">Easy — makes mistakes</option>
            <option value="medium">Medium — plays fair</option>
            <option value="hard">Hard — unbeatable</option>
          </select>
        </label>
        <label>You play
          <select value={playerSymbol} onChange={(e) => reset(e.target.value)} data-testid="solo-oxo-symbol">
            <option value="X">X (go first)</option>
            <option value="O">O (Bot goes first)</option>
          </select>
        </label>
      </div>

      <div className={`solo-status ${result?.winner === playerSymbol ? 'win' : result?.winner === bot ? 'lose' : result?.winner === 'draw' ? 'draw' : ''}`}
        data-testid="solo-oxo-status">{statusLine}</div>

      <div className="oxo-board" data-testid="solo-oxo-board">
        {board.map((c, i) => (
          <button key={i} data-testid={`solo-oxo-cell-${i}`}
            className={`oxo-cell ${c ? 'filled ' + c.toLowerCase() : ''} ${result?.line?.includes(i) ? 'win' : ''}`}
            disabled={!!c || !myTurn || !!result}
            onClick={() => play(i)}>
            {c}
          </button>
        ))}
      </div>

      <div className="solo-actions">
        <button className="btn" data-testid="solo-oxo-rematch" onClick={() => reset()}><RotateCcw size={15} /> {result ? 'Play again' : 'Restart'}</button>
        {(score.wins + score.losses + score.draws) > 0 && (
          <button className="btn ghost" data-testid="solo-oxo-reset-score" onClick={resetScore}><Trophy size={15} /> Reset scoreboard</button>
        )}
      </div>
    </div>
  );
}
